/**
 * Lead-display fallback helpers — single source of truth for the
 * "what do we actually render?" question across Leads list, Pipeline
 * cards, Lead Focus, and Additional Contacts.
 *
 * Why these exist:
 *   1) Specialty was rendered three different ways across surfaces:
 *      `lead.specialty` on the Leads list and Lead-Focus sidebar,
 *      `specialty_multi[0]` in some sections, plain truthy-fallback in
 *      others. Real-mode imports populate `specialty_multi` (multi-
 *      select array) but leave the legacy `specialty` scalar empty —
 *      so half the surfaces showed `—` for fully-populated leads.
 *   2) Imported leads occasionally carry literal `"0"` (or `"n/a"`,
 *      `"--"`) in the company column. Direct truthy fallbacks treat
 *      those as valid strings and render them, which looks like a bug
 *      to the operator. `cleanScalar()` filters those at the display
 *      boundary without mutating storage.
 *
 * Keep these helpers pure — no state, no I/O. They're called inline
 * in JSX and must stay cheap.
 */

const JUNK_SCALARS = new Set([
  "", "0", "n/a", "na", "none", "null", "-", "--",
]);

/** Strip whitespace + filter known junk sentinels; returns "" for junk. */
export function cleanScalar(v) {
  if (v == null) return "";
  const s = String(v).trim();
  return JUNK_SCALARS.has(s.toLowerCase()) ? "" : s;
}

/**
 * Specialty display rule (locked Jun 2026, iter 166; updated Feb 2026 iter 178.7):
 *   enrichment.nppes.primary_specialty  // ← authoritative NPPES truth
 *   || nppes.specialty                   // ← legacy nested NPPES blob
 *   || first NON-VANDALIZED specialty_multi entry  // ← prefer real over stamped
 *   || first specialty_multi entry       // ← fallback (single-value case)
 *   || specialty                         // ← legacy CSV scalar (LAST RESORT
 *                                            because Ampliz/Gartner imports
 *                                            stamped "Internal Medicine" on
 *                                            every row, drowning real signal)
 *   || "—"
 *
 * Iter 178.7 fix (Stephanie #adc7af38 — "All leads marked Internal Medicine"):
 * the Ampliz/Gartner imports stamped both `lead.specialty` AND
 * `lead.specialty_multi[0]` = "Internal Medicine", then appended the REAL
 * specialties at index 1+. So Iter 166's fix (read multi[0]) still surfaced
 * the lie on the list. Now we look at the FULL multi array and prefer
 * the first entry that ISN'T a known vandalized-default, but only when
 * the array has 2+ distinct entries (single-value rows where "Internal
 * Medicine" really IS the specialty stay correct).
 */
// Known auto-stamp values dropped by Phase-2 imports.
const _VANDALIZED_SPECIALTIES = new Set([
  "internal medicine",  // Ampliz / Gartner default
]);
export function specialtyDisplay(lead) {
  if (!lead) return "—";
  const nppesPrimary = cleanScalar(lead.enrichment?.nppes?.primary_specialty);
  if (nppesPrimary) return nppesPrimary;
  const nppesNested = cleanScalar(lead.nppes?.specialty);
  if (nppesNested) return nppesNested;
  const arr = Array.isArray(lead.specialty_multi) ? lead.specialty_multi : null;
  if (arr && arr.length) {
    // Build a deduped (case-insensitive) cleaned list, preserving order.
    const seen = new Set();
    const cleaned = [];
    for (const v of arr) {
      const s = cleanScalar(v);
      if (!s) continue;
      const key = s.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      cleaned.push(s);
    }
    if (cleaned.length > 1) {
      // Prefer the first non-vandalized entry. If every entry is
      // vandalized, fall through to cleaned[0] (current behavior).
      const real = cleaned.find(s => !_VANDALIZED_SPECIALTIES.has(s.toLowerCase()));
      if (real) return real;
    }
    if (cleaned.length) return cleaned[0];
  }
  const scalar = cleanScalar(lead.specialty);
  if (scalar) return scalar;
  return "—";
}

/**
 * Company display rule:
 *   cleanScalar(company) || cleanScalar(title) || cleanScalar(practice) || "—"
 *
 * `title` is the legacy lead-record name (often the company); `practice`
 * is set by older import paths. `cleanScalar` filters the literal `"0"`
 * sentinel so it never reaches the UI as a label.
 */
export function companyDisplay(lead) {
  if (!lead) return "—";
  return (
    cleanScalar(lead.company) ||
    cleanScalar(lead.title) ||
    cleanScalar(lead.practice) ||
    "—"
  );
}

/** Same as companyDisplay but returns "" instead of "—" — useful when
 *  you want to compose a label and add a separator only if there's
 *  actually a company name. */
export function companyDisplayBlank(lead) {
  const v = companyDisplay(lead);
  return v === "—" ? "" : v;
}
